/**
 * Petits utilitaires pour les blocs dont le contenu vient du CRM.
 *
 * Règle commune à tous ces blocs : une valeur absente OU VIDE retombe sur
 * celle d'origine, codée dans le composant. C'est ce qui permet de rendre
 * une section éditable sans jamais risquer de la vider — ni par une fausse
 * manœuvre dans l'éditeur, ni parce que le CRM est injoignable.
 */

/** Renvoie `valeur` si c'est une chaîne non vide, sinon `defaut`. */
export function ou(valeur: unknown, defaut: string): string {
  return typeof valeur === 'string' && valeur.trim() !== '' ? valeur : defaut
}

/** Idem pour un nombre — utile aux calibrages de logos. */
export function ouNombre(valeur: unknown, defaut: number): number {
  return typeof valeur === 'number' && Number.isFinite(valeur) ? valeur : defaut
}

/** Récupère le contenu d'une section par son id, sans planter si elle manque. */
export function contenuDe<T>(
  sections: Array<{ id: string; content?: unknown }>,
  id: string,
): T | undefined {
  return sections.find((s) => s.id === id)?.content as T | undefined
}

export type TuileMosaique = { src: string; o: 'portrait' | 'paysage'; pos?: string }

/**
 * Construit les images d'une mosaïque à partir du contenu CRM.
 *
 * Chaque emplacement a une FORME FIXE (imposée par la grille éditoriale) ; le
 * client choisit son image ET son cadrage (point de focus → object-position).
 * La valeur d'un emplacement peut être une simple URL (ancien format) ou un
 * objet `{ src, pos }`. Un emplacement vide retombe sur l'image d'origine.
 */
export function mosaiqueTuiles(
  content: Record<string, unknown> | undefined,
  defauts: TuileMosaique[],
): TuileMosaique[] {
  return defauts.map((d, i) => {
    const v = content?.[`tuile${i + 1}`]
    let src = ''
    let pos: string | undefined
    if (typeof v === 'string') {
      src = v
    } else if (v && typeof v === 'object') {
      const o = v as Record<string, unknown>
      if (typeof o.src === 'string') src = o.src
      if (typeof o.pos === 'string') pos = o.pos
    }
    return { src: src.trim() !== '' ? src : d.src, o: d.o, pos: pos ?? d.pos }
  })
}

/**
 * Normalise un champ image CRM susceptible de porter un cadrage (focal
 * point). Ancien format : simple URL (string). Nouveau format : objet
 * `{ src, pos }` où `pos` est une position CSS (`object-position` /
 * `background-position`, ex. `"50% 50%"`). Ne lève jamais.
 */
export function imageFocal(v: unknown): { src: string; pos: string } {
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>
    return { src: typeof o.src === 'string' ? o.src : '', pos: typeof o.pos === 'string' ? o.pos : '50% 50%' }
  }
  return { src: typeof v === 'string' ? v : '', pos: '50% 50%' }
}
